import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Video, Save, Loader2, ExternalLink, Trash2, Play, Info } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminHeroVideo = () => {
  const { getAuthAxios } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    hero_video_url: "",
    hero_video_type: "url"
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const authAxios = getAuthAxios();
      const response = await authAxios.get(`${API}/admin/settings`);
      setSettings({
        hero_video_url: response.data.hero_video_url || "",
        hero_video_type: response.data.hero_video_type || "url"
      });
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const authAxios = getAuthAxios();
      await authAxios.put(`${API}/admin/settings`, settings);
      toast.success("Hero video settings saved!");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm("Are you sure you want to remove the hero video?")) return;
    
    setSaving(true);
    try {
      const authAxios = getAuthAxios();
      await authAxios.put(`${API}/admin/settings`, {
        hero_video_url: null,
        hero_video_type: "url"
      });
      setSettings({ hero_video_url: "", hero_video_type: "url" });
      toast.success("Hero video removed!");
    } catch (error) {
      toast.error("Failed to remove video");
    } finally {
      setSaving(false);
    }
  };

  const getVideoEmbed = () => {
    const url = settings.hero_video_url;
    if (!url) return null;

    if (settings.hero_video_type === "youtube") {
      // Extract YouTube video ID
      const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
      if (match) {
        return `https://www.youtube.com/embed/${match[1]}?autoplay=1&mute=1&loop=1&playlist=${match[1]}`;
      }
    }
    
    if (settings.hero_video_type === "vimeo") {
      // Extract Vimeo video ID
      const match = url.match(/vimeo\.com\/(\d+)/);
      if (match) {
        return `https://player.vimeo.com/video/${match[1]}?autoplay=1&muted=1&loop=1`;
      }
    }

    return url;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF3B30]" />
      </div>
    );
  }

  return (
    <div data-testid="admin-hero-video">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#0A0A0A] font-['Outfit']">Hero Video</h1>
        <p className="text-[#4B5563]">Set the background video for the homepage hero section</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Settings Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Video className="w-5 h-5 text-[#FF3B30]" />
              Video Settings
            </CardTitle>
            <CardDescription>
              Add a video URL to display in the homepage hero section
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label>Video Type</Label>
              <Select 
                value={settings.hero_video_type} 
                onValueChange={(v) => setSettings(prev => ({ ...prev, hero_video_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="url">Direct URL (MP4)</SelectItem>
                  <SelectItem value="youtube">YouTube</SelectItem>
                  <SelectItem value="vimeo">Vimeo</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Video URL</Label>
              <Input 
                placeholder={
                  settings.hero_video_type === "youtube" 
                    ? "https://youtube.com/watch?v=..." 
                    : settings.hero_video_type === "vimeo"
                    ? "https://vimeo.com/..."
                    : "https://example.com/video.mp4"
                }
                value={settings.hero_video_url}
                onChange={(e) => setSettings(prev => ({ ...prev, hero_video_url: e.target.value }))}
              />
            </div>

            {/* URL Format Warning */}
            {settings.hero_video_url && settings.hero_video_type === "url" && !settings.hero_video_url.match(/\.(mp4|webm|mov|ogg)($|\?)/i) && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-red-600 mt-0.5" />
                  <div className="text-sm text-red-800">
                    <p className="font-medium mb-1">⚠️ This might not be a valid video URL</p>
                    <p className="text-red-700">
                      Direct video URLs should end with .mp4, .webm, or .mov
                      <br />
                      Links from Kapwing, Google Drive, or similar platforms won't work directly.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Tips */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-2">⚠️ Important - What URLs Work:</p>
                  <ul className="space-y-1 text-blue-700">
                    <li><strong>Direct MP4:</strong> URL must end in .mp4 (e.g., from Cloudinary, S3, or video CDNs)</li>
                    <li><strong>YouTube:</strong> Use youtube.com/watch?v=... or youtu.be/... links</li>
                    <li><strong>Vimeo:</strong> Use vimeo.com/... links</li>
                  </ul>
                  <p className="mt-2 text-red-700 font-medium">
                    ❌ Links from Kapwing, Google Drive, Dropbox WON'T work as they're not direct video files.
                  </p>
                  <p className="mt-2">
                    💡 <strong>Tip:</strong> Upload your video to YouTube (unlisted) and use the YouTube link, or use Cloudinary for direct MP4 hosting.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button 
                onClick={handleSave} 
                className="flex-1 btn-primary"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Settings
                  </>
                )}
              </Button>
              {settings.hero_video_url && (
                <Button 
                  onClick={handleClear} 
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  disabled={saving}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Preview */}
        <Card>
          <CardHeader>
            <CardTitle>Preview</CardTitle>
            <CardDescription>
              How the video will appear on the homepage
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative aspect-video bg-[#111827] rounded-xl overflow-hidden">
              {settings.hero_video_url ? (
                settings.hero_video_type === "url" ? (
                  <video 
                    src={settings.hero_video_url}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <iframe
                    src={getVideoEmbed()}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="autoplay; fullscreen"
                    allowFullScreen
                  />
                )
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500">
                  <Video className="w-16 h-16 mb-4" />
                  <p className="text-center">No video set<br/>Add a URL to preview</p>
                </div>
              )}
              
              {/* Overlay preview */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent pointer-events-none" />
              
              {/* Sample content overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-white">
                  <p className="text-xs text-[#FFD700] mb-2 tracking-wider">PREVIEW</p>
                  <h2 className="text-2xl font-bold font-['Outfit']">THE FINRUN</h2>
                  <p className="text-sm text-gray-300">Chennai 2026</p>
                </div>
              </div>
            </div>

            {settings.hero_video_url && (
              <div className="mt-4 flex items-center justify-between">
                <p className="text-sm text-[#4B5563] truncate flex-1 mr-4">
                  {settings.hero_video_url}
                </p>
                <a 
                  href={settings.hero_video_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#007AFF] hover:underline text-sm flex items-center gap-1"
                >
                  <ExternalLink className="w-4 h-4" />
                  Open
                </a>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Sample Videos */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle>Sample Video Sources</CardTitle>
          <CardDescription>
            Free stock video sites you can use
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { name: "Pexels Videos", url: "https://www.pexels.com/videos/", desc: "Free stock videos" },
              { name: "Pixabay Videos", url: "https://pixabay.com/videos/", desc: "Free video clips" },
              { name: "Coverr", url: "https://coverr.co/", desc: "Free background videos" }
            ].map((source, i) => (
              <a 
                key={i}
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block p-4 bg-[#FAFAFA] rounded-xl hover:bg-gray-100 transition-colors"
              >
                <p className="font-medium text-[#0A0A0A] mb-1">{source.name}</p>
                <p className="text-sm text-[#4B5563]">{source.desc}</p>
              </a>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminHeroVideo;
